/**
* Multi-site config resolver.
*
* Chooses a base config by `VITE_SITE_ID` (or `SITE_ID` on the server), then
* layers on env-var overrides so a single build can be repointed to a
* different site by swapping env vars — no rebuild required.
*/
import { defaultSiteConfig, type SiteConfig } from "./default";

const REGISTRY: Record<string, SiteConfig> = {
  default: defaultSiteConfig,
};

function envVar(name: string): string | undefined {
  if (typeof process !== "undefined" && process.env && process.env[name]) {
    return process.env[name];
  }
  const meta = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return meta?.[name];
}

function pickSiteId(): string {
  return envVar("VITE_SITE_ID") ?? envVar("SITE_ID") ?? "default";
}

function applyOverrides(base: SiteConfig): SiteConfig {
  const clone: SiteConfig = JSON.parse(JSON.stringify(base));
  const set = (fn: (c: SiteConfig) => void) => fn(clone);

  const name = envVar("VITE_SITE_NAME") ?? envVar("SITE_NAME");
  if (name) set((c) => { c.name = name; });

  const canonical = envVar("VITE_SITE_CANONICAL_HOST") ?? envVar("SITE_CANONICAL_HOST");
  if (canonical) set((c) => { c.canonicalHost = canonical; });

  const twitter = envVar("VITE_SITE_TWITTER") ?? envVar("SITE_TWITTER");
  if (twitter) set((c) => { c.social.twitter = twitter; });

  const google = envVar("VITE_GOOGLE_SITE_VERIFICATION") ?? envVar("GOOGLE_SITE_VERIFICATION");
  if (google) set((c) => { c.verification.google = google; });

  const bing = envVar("VITE_BING_SITE_VERIFICATION") ?? envVar("BING_SITE_VERIFICATION");
  if (bing) set((c) => { c.verification.bing = bing; });

  const gsc = envVar("VITE_SITE_GSC_PROPERTY") ?? envVar("SITE_GSC_PROPERTY");
  if (gsc) set((c) => { c.searchConsole.siteUrl = gsc; });


  return clone;
}

/** The resolved, environment-aware site config. */
export const SITE: SiteConfig = applyOverrides(
  REGISTRY[pickSiteId()] ?? defaultSiteConfig,
);

/** Resolve the canonical host for a given locale (or default). */
export function hostForLocale(localeCode?: string): string {
  const loc = SITE.locales.find((l) => l.code === (localeCode ?? SITE.defaultLocale));
  return loc?.host || SITE.canonicalHost;
}

/**
* Percent-encode non-ASCII characters in a path (e.g. Thai slugs) so the
* result is a valid URL for sitemaps, canonicals and og:url. Already-encoded
* sequences (%E0%B8...) are preserved; query/hash are left intact.
*/
export function encodeUrlPath(path: string): string {
  const [pathname, ...rest] = path.split(/(?=[?#])/);
  const encoded = pathname
    .split("/")
    .map((seg) => {
      if (!/[^\x20-\x7E]/.test(seg)) return seg;
      // decode first to avoid double-encoding, then encode
      let decoded = seg;
      try {
        decoded = decodeURIComponent(seg);
      } catch {
        /* keep raw */
      }
      return encodeURIComponent(decoded);
    })
    .join("/");
  return encoded + rest.join("");
}

/**
* Build an absolute URL. Prefers per-locale host, then site canonicalHost,
* then falls back to the request host (for preview deployments).
*/
export function absoluteUrl(path: string, host?: string | null, localeCode?: string): string {
  const raw = path.startsWith("/") ? path : `/${path}`;
  const clean = encodeUrlPath(raw);
  const canonical = hostForLocale(localeCode);
  if (canonical) {
    const proto = canonical.startsWith("localhost") ? "http" : "https";
    return `${proto}://${canonical}${clean}`;
  }
  if (!host) return clean;
  const proto = host.startsWith("localhost") ? "http" : "https";
  return `${proto}://${host}${clean}`;
}


/** Prefix a path with the locale's `pathPrefix` (empty for default). */
export function localizedPath(path: string, localeCode: string): string {
  const loc = SITE.locales.find((l) => l.code === localeCode);
  const prefix = loc?.pathPrefix ?? "";
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (!prefix) return clean;
  const p = prefix.startsWith("/") ? prefix : `/${prefix}`;
  return `${p}${clean === "/" ? "" : clean}`;
}

export type { SiteConfig };

/**
* Ensure any asset URL (CDN, relative, or already-absolute) becomes an
* absolute https URL suitable for og:image / twitter:image tags. Social
* scrapers reject relative paths — always emit the absolute form.
*/
export function absoluteAsset(url: string, host?: string | null): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return absoluteUrl(url, host);
}

